import { useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { SkDatePicker } from "./SkDatePicker";
import { ClipboardCheck, Loader2, Send, CheckCircle2, Clock, AlertTriangle, PenLine, RotateCcw, Check, Eye } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const EMPTY = { kind: "verified", subject_name: "", subject_email: "", card_id: "", area: "", description: "", audit_date: "", result: "", knowledge_level: "" };

const LEVELS = [
  { v: "beginner", label: "Začiatočník" },
  { v: "advanced", label: "Pokročilý" },
  { v: "expert", label: "Vysoká úroveň znalostí" },
];

const KINDS = [
  { v: "verified", label: "Preverený (skúsený)", hint: "Sa.Eko.Verified." },
  { v: "graduate", label: "Absolvent (zaučený) — Work, learn and earn©", hint: "Sa.Eko.Con." },
  { v: "training", label: "Absolvent Tréningového centra", hint: "Sa.Eko.Train." },
];

const LBL = {
  verified: { who: "Meno overovaného *", email: "E-mail overovaného (pre zaslanie potvrdenia)", date: "Dátum preverovania", desc: "Popis preverovania *" },
  graduate: { who: "Meno absolventa *", email: "E-mail absolventa (pre zaslanie potvrdenia)", date: "Dátum ukončenia programu", desc: "Popis zaučenia *" },
  training: { who: "Meno absolventa *", email: "E-mail absolventa (pre zaslanie potvrdenia)", date: "Dátum ukončenia doškolenia", desc: "Rozsah doškolenia *" },
};

const STATUS = {
  pending: { label: "Čaká na potvrdenie admina", cls: "text-amber-300 border-amber-400/40 bg-amber-400/5", Icon: Clock },
  confirmed: { label: "Potvrdené — PDF odoslané", cls: "text-[#F2E3C6] border-[#D4AF37]/40 bg-[#D4AF37]/5", Icon: CheckCircle2 },
  returned: { label: "Vrátené na opravu", cls: "text-red-300 border-red-400/40 bg-red-400/5", Icon: AlertTriangle },
};

export default function MentorAudits({ token }) {
  const auth = { headers: { Authorization: `Bearer ${token}` } };
  const [me, setMe] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [list, setList] = useState([]);
  const [busy, setBusy] = useState(false);
  const [busyPrev, setBusyPrev] = useState(false);
  const [msg, setMsg] = useState(null);
  const [editId, setEditId] = useState("");
  const formRef = useRef(null);
  const startFix = (a) => {
    setForm({ kind: a.kind || "verified", subject_name: a.subject_name || "", subject_email: a.subject_email || "", card_id: a.card_id || "",
      area: a.area || "", description: a.description || "", audit_date: (a.audit_date || "").slice(0, 10), result: a.result || "", knowledge_level: a.knowledge_level || "" });
    setEditId(a.id); setMsg(null);
    setTimeout(() => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 50);
  };

  // Elektronický podpis mentora
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const hasInk = useRef(false);
  const [savedSig, setSavedSig] = useState("");
  const [editing, setEditing] = useState(false);
  const [savingSig, setSavingSig] = useState(false);
  const [inked, setInked] = useState(false);

  const set = (k) => (e) => { setForm((f) => ({ ...f, [k]: e.target.value })); setMsg(null); };

  const load = useCallback(async () => {
    try {
      const m = await axios.get(`${API}/mentor/me`, auth);
      setMe(m.data);
      if (m.data.is_mentor) {
        const [r, s] = await Promise.all([
          axios.get(`${API}/mentor/audits`, auth),
          axios.get(`${API}/applicant/signature`, auth),
        ]);
        setList(r.data.audits || []);
        setSavedSig(s.data.signature || "");
        setEditing(!s.data.has_signature);
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  useEffect(() => { load(); }, [load]);

  const point = (e) => {
    const c = canvasRef.current;
    const r = c.getBoundingClientRect();
    const src = e.touches ? e.touches[0] : e;
    return { x: (src.clientX - r.left) * (c.width / r.width), y: (src.clientY - r.top) * (c.height / r.height) };
  };
  const startDraw = (e) => {
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineWidth = 2.4; ctx.lineCap = "round"; ctx.lineJoin = "round"; ctx.strokeStyle = "#111111";
    drawing.current = true;
    const p = point(e); ctx.beginPath(); ctx.moveTo(p.x, p.y);
  };
  const moveDraw = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = point(e); ctx.lineTo(p.x, p.y); ctx.stroke(); hasInk.current = true; setInked(true);
  };
  const endDraw = () => { drawing.current = false; };
  const clearPad = () => { const c = canvasRef.current; if (c) c.getContext("2d").clearRect(0, 0, c.width, c.height); hasInk.current = false; setInked(false); };

  const saveSig = async () => {
    if (!hasInk.current) { setMsg({ type: "err", text: "Najprv sa podpíšte do poľa." }); return; }
    setSavingSig(true); setMsg(null);
    try {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await axios.post(`${API}/applicant/signature`, { signature: dataUrl }, auth);
      setSavedSig(dataUrl); setEditing(false);
    } catch { setMsg({ type: "err", text: "Podpis sa nepodarilo uložiť." }); }
    finally { setSavingSig(false); }
  };

  if (!me || !me.is_mentor) return null;

  // Podpis sa priloží priamo pri odoslaní: ak je na pode nakreslený, uloží sa automaticky.
  const ensureSignature = async () => {
    if (editing && hasInk.current) {
      const dataUrl = canvasRef.current.toDataURL("image/png");
      await axios.post(`${API}/applicant/signature`, { signature: dataUrl }, auth);
      setSavedSig(dataUrl); setEditing(false);
      return true;
    }
    if (!editing && savedSig) return true;
    setEditing(true);
    setMsg({ type: "err", text: "Najprv sa podpíšte do poľa — podpis sa priloží pri odoslaní." });
    return false;
  };

  const submit = async () => {
    setBusy(true); setMsg(null);
    try {
      if (!(await ensureSignature())) { setBusy(false); return; }
      if (editId) await axios.put(`${API}/mentor/audits/${editId}`, form, auth);
      else await axios.post(`${API}/mentor/audits`, form, auth);
      setMsg({ type: "ok", text: editId ? "Opravené potvrdenie odoslané adminovi znova na podpis." : "Audit s vaším podpisom odoslaný adminovi na potvrdenie." });
      setForm(EMPTY); setEditId(""); load();
    } catch (e) {
      setMsg({ type: "err", text: e?.response?.data?.detail || "Odoslanie zlyhalo." });
    } finally { setBusy(false); }
  };

  const hasUsableSig = (!editing && !!savedSig) || (editing && inked);
  const canSend = hasUsableSig && form.subject_name.trim().length >= 2 && form.card_id.trim() && form.description.trim().length >= 5;

  const previewCert = async () => {
    setBusyPrev(true); setMsg(null);
    try {
      if (!(await ensureSignature())) { setBusyPrev(false); return; }
      const r = await axios.post(`${API}/mentor/audits/preview`, editId ? { ...form, audit_id: editId } : form, { ...auth, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      window.open(url, "_blank", "noopener");
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch {
      setMsg({ type: "err", text: "Náhľad sa nepodarilo vytvoriť. Skontrolujte podpis a údaje." });
    } finally { setBusyPrev(false); }
  };

  return (
    <div className="mt-10 border border-[#D4AF37]/25 bg-[#0a0a0a] p-6" data-testid="mentor-audits">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardCheck size={20} className="text-[#D4AF37]" />
        <h3 className="font-display text-xl">Audity — mentor SauberEko®</h3>
      </div>
      <p className="text-white/40 text-sm mb-5">Zapíšte preverenie alebo zaučenie (viaže sa na Card ID karty). Pri odoslaní sa automaticky priradí evidenčné číslo a pripojí sa váš elektronický podpis. Po odoslaní ho potvrdí admin svojím podpisom a razítkom a vygeneruje sa podpísaný certifikát; do Siene slávy sa zapíše až po schválení.</p>

      {/* Elektronický podpis mentora — povinný pred odoslaním */}
      <div className="mb-6 border border-[#D4AF37]/20 bg-black/40 p-4" data-testid="mentor-signature-block">
        <div className="flex items-center gap-2 mb-2">
          <PenLine size={16} className="text-[#D4AF37]" />
          <h4 className="text-white/80 text-sm font-medium">Váš elektronický podpis (povinný)</h4>
        </div>
        {editing ? (
          <div>
            <p className="text-white/50 text-xs mb-2">Podpíšte sa myšou alebo prstom — podpis sa pripojí na certifikát nad vaše meno. Stačí sa podpísať a kliknúť „Podpísať a odoslať"; podpis sa priloží automaticky.</p>
            <canvas
              ref={canvasRef} width={520} height={160} data-testid="mentor-signature-pad"
              onMouseDown={startDraw} onMouseMove={moveDraw} onMouseUp={endDraw} onMouseLeave={endDraw}
              onTouchStart={startDraw} onTouchMove={moveDraw} onTouchEnd={endDraw}
              className="w-full h-[160px] rounded-md bg-white cursor-crosshair touch-none"
              style={{ touchAction: "none" }} />
            <div className="flex items-center gap-3 mt-3">
              <button onClick={saveSig} disabled={savingSig} data-testid="mentor-signature-save"
                className="inline-flex items-center gap-2 border border-[#D4AF37]/50 text-[#F2E3C6] font-semibold px-5 py-2.5 text-sm hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-60">
                {savingSig ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />} Uložiť podpis (voliteľné)
              </button>
              <button onClick={clearPad} data-testid="mentor-signature-clear"
                className="inline-flex items-center gap-2 border border-white/15 text-white/70 px-4 py-2.5 text-sm hover:border-white/40 transition-colors">
                <RotateCcw size={14} /> Vymazať
              </button>
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-4 border border-[#D4AF37]/25 bg-[#D4AF37]/5 p-3 rounded-md">
            <div className="flex items-center gap-4 min-w-0">
              <div className="bg-white rounded px-3 py-1 shrink-0">
                <img src={savedSig} alt="podpis" className="h-10 object-contain" data-testid="mentor-signature-preview" />
              </div>
              <span className="inline-flex items-center gap-1.5 text-green-400 text-sm font-medium">
                <Check size={16} /> Podpis uložený
              </span>
            </div>
            <button onClick={() => { setEditing(true); setTimeout(clearPad, 50); }} data-testid="mentor-signature-change"
              className="inline-flex items-center gap-2 text-[#D4AF37] text-xs font-semibold shrink-0 hover:underline">
              <PenLine size={14} /> Zmeniť podpis
            </button>
          </div>
        )}
      </div>

      <div ref={formRef} />
      {editId && (
        <div data-testid="audit-edit-banner" className="mb-5 flex items-center justify-between gap-3 border border-red-400/40 bg-red-400/5 px-4 py-3 text-sm">
          <span className="text-red-200">Opravujete vrátené potvrdenie <b>{list.find((x) => x.id === editId)?.record_no}</b>. Evidenčné číslo ostáva rovnaké.</span>
          <button type="button" onClick={() => { setEditId(""); setForm(EMPTY); }} data-testid="audit-edit-cancel" className="text-white/60 hover:text-white text-xs shrink-0">Zrušiť opravu</button>
        </div>
      )}
      <div className="mb-5" data-testid="audit-kind">
        <label className="block text-xs text-white/50 mb-2">Typ záznamu *</label>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {KINDS.map((k) => (
            <button key={k.v} type="button" data-testid={`audit-kind-${k.v}`}
              onClick={() => { if (!editId) { setForm((f) => ({ ...f, kind: k.v })); setMsg(null); } }}
              className={`text-left px-4 py-3 border transition-colors ${form.kind === k.v ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/15 text-white/60 hover:border-[#D4AF37]/50"}`}>
              <span className="block text-sm font-medium">{k.label}</span>
              <span className="block text-[11px] text-[#D4AF37]/80 mt-0.5">č. {k.hint} 2026-…</span>
            </button>
          ))}
        </div>
      </div>

      <div className="mb-5" data-testid="audit-level">
        <label className="block text-xs text-white/50 mb-2">Úroveň znalostí *</label>
        <div className="grid grid-cols-3 gap-3">
          {LEVELS.map((l) => (
            <button key={l.v} type="button" data-testid={`audit-level-${l.v}`}
              onClick={() => { setForm((f) => ({ ...f, knowledge_level: l.v })); setMsg(null); }}
              className={`px-3 py-2.5 text-sm border transition-colors ${form.knowledge_level === l.v ? "border-[#D4AF37] bg-[#D4AF37]/10 text-white" : "border-white/15 text-white/60 hover:border-[#D4AF37]/50"}`}>
              {l.label}
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <F label={(LBL[form.kind] || LBL.verified).who} testid="audit-subject" value={form.subject_name} onChange={set("subject_name")} placeholder="Ján Novák" />
        <F label={(LBL[form.kind] || LBL.verified).email} testid="audit-subject-email" type="email" value={form.subject_email} onChange={set("subject_email")} placeholder="jan@email.sk" />
        <F label="Card ID (číslo karty) *" testid="audit-cardid" value={form.card_id} onChange={set("card_id")} placeholder="0281845" />
        <F label="Oblasť / odbor" testid="audit-area" value={form.area} onChange={set("area")} placeholder="Zváranie potrubí" />
        <SkDatePicker label={(LBL[form.kind] || LBL.verified).date} testid="audit-date" value={form.audit_date} onChange={(v) => { setForm((f) => ({ ...f, audit_date: v })); setMsg(null); }} />
        <F label="Výsledok" testid="audit-result" value={form.result} onChange={set("result")} placeholder="Vyhovel / Nevyhovel" />
      </div>
      <div className="mt-4">
        <label className="block text-xs text-white/50 mb-1.5">{(LBL[form.kind] || LBL.verified).desc}</label>
        <textarea data-testid="audit-description" value={form.description} onChange={set("description")} rows={4} maxLength={4000}
          className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none resize-none"
          placeholder="Dňa … bol menovaný prevererný z teoretickej a praktickej časti vo zváraní potrubí …" />
      </div>

      {!hasUsableSig && (
        <div data-testid="mentor-signature-required" className="flex items-center gap-2 text-sm px-3 py-2 mt-4 border border-amber-400/40 text-amber-300 bg-amber-400/5">
          <AlertTriangle size={15} /> <span>Podpíšte sa do poľa vyššie — podpis sa priloží pri odoslaní.</span>
        </div>
      )}

      {msg && (
        <div data-testid="audit-msg" className={`flex items-center gap-2 text-sm px-3 py-2 mt-4 border ${msg.type === "ok" ? "border-[#D4AF37]/40 text-[#F2E3C6] bg-[#D4AF37]/5" : "border-red-500/40 text-red-300 bg-red-500/5"}`}>
          {msg.type === "ok" ? <CheckCircle2 size={15} /> : <AlertTriangle size={15} />} <span>{msg.text}</span>
        </div>
      )}

      <div className="mt-5 flex flex-wrap items-center gap-3">
        <button onClick={previewCert} disabled={!canSend || busyPrev} data-testid="audit-preview-btn"
          className="inline-flex items-center gap-2 border border-[#D4AF37]/50 text-[#F2E3C6] px-5 py-2.5 text-sm hover:bg-[#D4AF37]/10 transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          {busyPrev ? <Loader2 size={15} className="animate-spin" /> : <Eye size={15} />} Náhľad certifikátu
        </button>
        <button onClick={submit} disabled={!canSend || busy} data-testid="audit-submit-btn"
          className="inline-flex items-center gap-2 bg-[#D4AF37] text-black font-semibold px-5 py-2.5 text-sm hover:bg-[#e5c14f] transition-colors disabled:opacity-40 disabled:cursor-not-allowed">
          {busy ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />} {editId ? "Opraviť a odoslať znova" : "Podpísať a odoslať adminovi"}
        </button>
      </div>

      {list.length > 0 && (
        <div className="mt-8" data-testid="mentor-audit-list">
          <h4 className="text-white/70 text-sm mb-3">Moje audity ({list.length})</h4>
          <div className="space-y-2">
            {list.map((a) => {
              const st = STATUS[a.status] || STATUS.pending;
              return (
                <div key={a.id} className="border border-white/10 px-4 py-3 flex items-center justify-between gap-3 flex-wrap">
                  <div className="min-w-0">
                    <p className="text-white text-sm font-medium truncate">{a.subject_name} <span className="text-white/40 font-normal">· Card ID {a.card_id}</span></p>
                    <p className="text-white/35 text-xs mt-0.5">
                      {a.record_no ? <span className="text-[#D4AF37]/80">{a.record_no} · </span> : null}
                      {a.kind === "graduate" ? "Absolvent WLE©" : a.kind === "training" ? "Absolvent Tréningového centra" : "Preverený"} · {a.area || "—"} · {a.audit_date ? new Date(a.audit_date.slice(0, 10)).toLocaleDateString("sk-SK") : "—"}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className={`inline-flex items-center gap-1.5 text-xs px-2.5 py-1 rounded-full border ${st.cls}`}><st.Icon size={12} /> {st.label}</span>
                    {a.status === "returned" && (
                      <button type="button" onClick={() => startFix(a)} data-testid={`audit-fix-${a.id}`}
                        className="inline-flex items-center gap-1.5 bg-[#D4AF37] text-black font-semibold px-3 py-1 text-xs hover:bg-[#e5c14f] transition-colors">
                        <PenLine size={12} /> Opraviť
                      </button>
                    )}
                  </div>
                  {a.status === "returned" && a.return_note && (
                    <p className="w-full text-red-200/90 text-xs border-t border-white/5 pt-2" data-testid={`audit-return-note-${a.id}`}><b>Poznámka admina:</b> {a.return_note}</p>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

const F = ({ label, testid, value, onChange, placeholder, type = "text" }) => (
  <div>
    <label className="block text-xs text-white/50 mb-1.5">{label}</label>
    <input type={type} data-testid={testid} value={value} onChange={onChange} placeholder={placeholder}
      className="w-full bg-black border border-white/15 px-3 py-2 text-sm text-white focus:border-[#D4AF37] outline-none" />
  </div>
);
